import { FIELD_META, type FieldMeta } from "@/features/profile/fieldMeta";
import type { EmployeeProfile, ProfileFileKind } from "@/features/profile/types";

/**
 * Building a change request from the "Ask HR to change" dialog.
 *
 * The dialog edits one section of the record at a time. What goes to HR is
 * the fields that actually differ from the record, so a reviewer reads real
 * changes, plus any document the section travels with.
 *
 * Everything the employee typed at sign-up can be asked for: an approved
 * record can still be wrong (a visa number typed where the Emirates ID number
 * belongs), and the employee is the one most likely to notice. The employee
 * number, the job, the joining and contract dates and the pay are set by HR,
 * and the API refuses a request for any of them.
 */

export type RequestSection =
  | "identity"
  | "contact"
  | "emergency"
  | "nomination"
  | "bank"
  | "emirates_id"
  | "passport"
  | "visa"
  | "labour_insurance"
  | "education"
  | "hours";

export interface RequestAttachment {
  /** The upload slot, which is also the route the file goes to first. */
  kind: ProfileFileKind;
  /** The profile field the stored file's id fills in the request. */
  field: string;
  label: string;
  hint: string;
  /** Must come with any change to the section: the bank's letter does. */
  required?: boolean;
}

export interface RequestSectionSpec {
  key: RequestSection;
  fields: readonly string[];
  /** Documents that go with the request: a new copy of the card, a letter. */
  attachments?: readonly RequestAttachment[];
}

const COPY_HINT = "Optional. Attach the new copy if the card itself changed. PDF or a clear photo.";

export const REQUEST_SECTIONS: readonly RequestSectionSpec[] = [
  {
    key: "identity",
    fields: [
      "full_name_en",
      "full_name_ar",
      "gender",
      "date_of_birth",
      "nationality",
      "marital_status",
    ],
  },
  {
    key: "contact",
    fields: [
      "personal_mobile",
      "whatsapp_number",
      "personal_email",
      "emirate_of_residence",
      "full_address",
    ],
  },
  {
    key: "emergency",
    fields: [
      "emergency_contact_name",
      "emergency_contact_relationship",
      "emergency_contact_mobile",
    ],
  },
  {
    key: "nomination",
    fields: [
      "nominee_name",
      "nominee_relationship",
      "nominee_nationality",
      "nominee_mobile",
      "nominee_address",
    ],
  },
  {
    // Payroll pays to this, so the bank's own letter comes with the request
    // and HR approves against it.
    key: "bank",
    fields: ["bank_name", "iban", "account_holder_name"],
    attachments: [
      {
        kind: "iban_letter",
        field: "iban_letter_file_id",
        label: "IBAN letter from the bank",
        hint: "PDF or a clear photo of the letter showing the IBAN and the account holder",
        required: true,
      },
    ],
  },
  {
    key: "emirates_id",
    fields: ["emirates_id_number", "emirates_id_issue", "emirates_id_expiry"],
    attachments: [
      {
        kind: "emirates_id_front",
        field: "emirates_id_front_file_id",
        label: "Emirates ID copy",
        hint: "Optional. Both sides on one page, as UAE PASS gives it.",
      },
    ],
  },
  {
    key: "passport",
    fields: ["passport_number", "passport_issue", "passport_expiry"],
    attachments: [
      { kind: "passport_copy", field: "passport_file_id", label: "Passport copy", hint: COPY_HINT },
    ],
  },
  {
    key: "visa",
    fields: ["visa_type", "visa_number", "visa_issue", "visa_expiry"],
    attachments: [
      { kind: "visa_copy", field: "visa_file_id", label: "Visa copy", hint: COPY_HINT },
    ],
  },
  {
    key: "labour_insurance",
    fields: [
      "labour_card_number",
      "labour_card_issue",
      "labour_card_expiry",
      "health_insurance_provider",
      "health_insurance_number",
      "health_insurance_issue",
      "health_insurance_expiry",
    ],
    attachments: [
      {
        kind: "labour_card",
        field: "labour_card_file_id",
        label: "Labour card copy",
        hint: COPY_HINT,
      },
      {
        kind: "health_insurance",
        field: "health_insurance_file_id",
        label: "Health insurance card",
        hint: COPY_HINT,
      },
    ],
  },
  {
    key: "education",
    fields: ["highest_education"],
    attachments: [
      { kind: "cv", field: "cv_file_id", label: "CV", hint: "Optional. A newer CV." },
      {
        kind: "degree_certificate",
        field: "degree_file_id",
        label: "Degree certificate",
        hint: "Optional. Attested, if the degree changed.",
      },
      {
        kind: "experience_letter",
        field: "experience_letter_file_id",
        label: "Experience letter",
        hint: "Optional.",
      },
    ],
  },
  {
    key: "hours",
    fields: ["hours_per_day", "weekly_off_days"],
  },
];

export function requestSection(key: RequestSection): RequestSectionSpec {
  return REQUEST_SECTIONS.find((section) => section.key === key)!;
}

/** How a field of the dialog is asked: the same kinds as HR's review. */
export function requestField(field: string): FieldMeta {
  return FIELD_META[field] ?? { key: field, label: field, kind: "text" };
}

/**
 * The form's starting values: what the record holds now, as strings.
 *
 * The days off are one question in two columns. The form holds whether they
 * rotate under `days_off_rotate` ("true" or "") and the weekdays as a list
 * like "5,6".
 */
export function requestFormFor(
  section: RequestSection,
  profile: EmployeeProfile,
): Record<string, string> {
  const current = profile as unknown as Record<string, unknown>;
  const form: Record<string, string> = {};
  for (const field of requestSection(section).fields) {
    if (field === "weekly_off_days") {
      form.days_off_rotate = profile.days_off_rotate ? "true" : "";
      form.weekly_off_days = (profile.weekly_off_days ?? []).join(",");
      continue;
    }
    form[field] = String(current[field] ?? "");
  }
  return form;
}

/** The weekdays the form holds, as numbers in order. */
export function weekdaysOf(value: string | undefined): number[] {
  return (value ?? "")
    .split(",")
    .map((day) => Number(day))
    .filter((day) => day >= 1 && day <= 7)
    .sort((a, b) => a - b);
}

export type RequestBuild =
  | { ok: true; changes: Record<string, unknown> }
  | { ok: false; reason: string; field?: string };

/**
 * What to send for one section, or why nothing can be sent yet.
 *
 * A field cleared in the form goes as null, which the API reads as "not
 * set", rather than as an empty string it would have to interpret. An
 * attachment's id is always a change when there is one: it is a new file.
 */
export function buildChangeRequest(
  section: RequestSection,
  profile: EmployeeProfile,
  form: Record<string, string>,
  attachmentIds: Readonly<Record<string, string>> = {},
): RequestBuild {
  const spec = requestSection(section);
  const current = profile as unknown as Record<string, unknown>;

  const changes: Record<string, unknown> = {};
  for (const field of spec.fields) {
    if (field === "weekly_off_days") {
      const rotates = form.days_off_rotate === "true";
      const days = rotates ? [] : weekdaysOf(form.weekly_off_days);
      if (rotates !== Boolean(profile.days_off_rotate)) changes.days_off_rotate = rotates;
      if (days.join(",") !== (profile.weekly_off_days ?? []).join(",")) {
        changes.weekly_off_days = days;
      }
      continue;
    }
    const typed = (form[field] ?? "").trim();
    const stored = String(current[field] ?? "");
    if (typed !== stored && !(typed !== "" && Number(typed) === Number(stored) && isFigure(field))) {
      changes[field] = typed === "" ? null : typed;
    }
  }
  for (const attachment of spec.attachments ?? []) {
    const id = attachmentIds[attachment.field];
    if (id) changes[attachment.field] = id;
  }

  if (Object.keys(changes).length === 0) {
    return { ok: false, reason: "Nothing has changed." };
  }

  // An account needs a name to be in: the one thing that becomes required
  // the moment an IBAN is given.
  if (section === "bank" && (form.iban ?? "").trim() && !(form.account_holder_name ?? "").trim()) {
    return {
      ok: false,
      reason: "Enter the name the account is in, as it appears on the bank's letter.",
      field: "account_holder_name",
    };
  }

  if (section === "hours" && form.days_off_rotate !== "true" && !weekdaysOf(form.weekly_off_days).length) {
    return { ok: false, reason: "Choose at least one day off.", field: "weekly_off_days" };
  }

  for (const attachment of spec.attachments ?? []) {
    if (attachment.required && !attachmentIds[attachment.field]) {
      return {
        ok: false,
        reason: `Attach the ${attachment.label} before sending.`,
        field: attachment.field,
      };
    }
  }

  return { ok: true, changes };
}

/** "8" and "8.00" hours are the same hours. */
function isFigure(field: string): boolean {
  return requestField(field).kind === "number";
}
